import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostListener, Input, Output } from '@angular/core';

import { DxcActiveFilter, DxcColumn, DxcIconName, DxcRowAction, DxcSelectOption, DxcSort } from '../dxc.types';
import { DxcIconComponent } from '../icon/dxc-icon.component';
import { dxcBelow } from '../media/dxc-media';
import { DxcScrollHintDirective } from '../scroll-hint/dxc-scroll-hint.directive';
import { DxcSelectComponent } from '../select/dxc-select.component';

export type DxcTableAction = {
    action: string;
    label: string;
    /**
     * Nombre del catálogo de `dxc-icon`, no un SVG.
     *
     * Era una cadena con el `<svg>` escrito a mano, inyectada con `[innerHTML]`.
     * Angular sanea todo lo que entra por ahí y **su lista de elementos
     * permitidos no incluye SVG**, así que el navegador recibía `<span></span>`:
     * los tres botones de la fila existían, medían sus 32x32 y no dibujaban
     * nada. No falla, no avisa — la columna «Acciones» simplemente sale vacía,
     * que es como se veía en Usuarios, Vehículos y Conductores a la vez.
     *
     * Con el nombre del catálogo el glifo lo dibuja `dxc-icon`, así que además
     * hereda la rejilla de 24 y el trazo 1.75 de la familia en vez de traer los
     * suyos (aquí, un trazo 2 que salía más pesado que el resto de la fila).
     */
    icon: DxcIconName;
    danger?: boolean;
    /**
     * Si la acción no aplica a ESTA fila —desactivar a un administrador, borrarse
     * a uno mismo—. La fila conserva el hueco del botón en vez de cerrar filas:
     * sin él, las acciones de las filas vecinas dejan de caer en la misma columna
     * y el lápiz de una queda debajo de la papelera de otra.
     */
    hidden?: (row: any) => boolean;
};

/**
 * Tabla del producto. Es donde el usuario pasa la mayor parte del tiempo, así
 * que decide cómo se percibe todo lo demás.
 *
 * Filtra, ordena, pagina y selecciona en cliente. Para colecciones grandes se
 * pasa `[serverSide]="true"` y la vista atiende `(sortChange)` y `(pageChange)`.
 *
 * Por debajo del marco: la caja avisa de que desplaza (`lg`), la primera columna
 * se queda fija (`md`), la barra de filtros se pliega tras un botón «Filtros» a
 * una hoja inferior (`md`) y, con `[stack]`, cada fila pasa a tarjeta (`sm`).
 * Cada celda lleva siempre `data-label` con el rótulo de su columna —un
 * atributo inerte, en escritorio no pinta nada— y `data-stack` con su papel.
 */
@Component({
    standalone: true,
    selector: 'dxc-table',
    templateUrl: './dxc-table.component.html',
    imports: [DxcIconComponent, DxcSelectComponent, DxcScrollHintDirective],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DxcTableComponent<T = any> {

    @Input() columns: DxcColumn<T>[] = [];
    @Input() set rows(rows: T[]) { this.#rows = rows ?? []; this.page = 1; }
    get rows() { return this.#rows; }

    @Input() actions: DxcTableAction[] = [];
    /**
     * Los anchos de `DxcColumn.width` mandan sobre el contenido.
     *
     * Va como prop y no deducido de que las columnas declaren `width`, porque
     * son dos cosas distintas: `width` es una preferencia que el reparto por
     * contenido puede desoír, y esto es la decisión de que no la desoiga. Se
     * enciende cuando alguna celda lleva prosa —un motivo, una nota—, que es
     * cuando el reparto por `max-content` deja de repartir y empieza a premiar
     * a la columna con la frase más larga.
     */
    @Input() fixedLayout = false;

    /**
     * En un teléfono (`sm`) cada fila se apila como tarjeta: la columna
     * `stack:'title'` arriba, el estado a su derecha y el resto en pares rótulo ·
     * valor (`DxcColumn.stack`). Opt-in porque solo sirve a un listado de
     * LECTURA: una tabla para comparar columnas, editable o anidada sigue
     * desplazándose de lado. En escritorio no cambia nada: la clase solo tiene
     * reglas bajo `sm`.
     */
    @Input() stack = false;

    @Input() pageSize = 20;
    @Input() selectable = false;
    @Input() loading = false;
    @Input() searchable = true;
    /**
     * Barra de filtros por columna, para las columnas que declaran `filter`.
     * Se combinan entre sí (Y lógica) y con la búsqueda si ambas están activas.
     */
    @Input() columnFilters = false;
    @Input() searchPlaceholder = 'Buscar…';
    @Input() itemLabel = 'registros';
    /**
     * Género de `itemLabel`, para que la barra de selección concuerde: «3
     * seleccionadas» con facturas, «3 seleccionados» con vehículos.
     */
    @Input() itemGender: 'f' | 'm' = 'f';
    @Input() emptyTitle = 'Todavía no hay nada aquí';
    @Input() emptyMessage = '';
    @Input() emptySearchTitle = 'Ningún resultado coincide con';
    @Input() showCount = true;
    @Input() toolbarExtra = false;
    /** Identidad de fila para `track`. Por defecto `_id` o `id`. */
    @Input() trackBy: (row: T) => unknown = (row: any) => row?._id ?? row?.id ?? row;
    /** Desactiva filtro/orden/paginación en cliente. */
    @Input() serverSide = false;

    @Input() selection: T[] = [];
    @Output() selectionChange = new EventEmitter<T[]>();
    @Output() rowAction = new EventEmitter<DxcRowAction<T>>();
    @Output() sortChange = new EventEmitter<DxcSort>();
    @Output() pageChange = new EventEmitter<number>();
    @Output() filtersChange = new EventEmitter<Record<string, string>>();

    #rows: T[] = [];
    query = '';
    /** Valor vigente de cada filtro, por clave de columna. */
    filters: Record<string, string> = {};
    sort: DxcSort | null = null;
    page = 1;

    /** Bajo `md` la barra de filtros vive en una hoja; esto dice si está abierta. */
    filtersOpen = false;
    /** Bajo `md`: el botón «Filtros» existe y la barra se pliega. */
    readonly compact = dxcBelow('md');

    constructor(private cdr: ChangeDetectorRef) { }

    /** Esc cierra la hoja de filtros, como cierra cualquier hoja del sistema. */
    @HostListener('document:keydown.escape')
    onEscape() {
        if (this.filtersOpen) { this.filtersOpen = false; this.cdr.markForCheck(); }
    }

    toggleFilters(open = !this.filtersOpen) { this.filtersOpen = open; }

    /** Papel de la celda en la tabla apilada; una columna de estado va a la derecha sola. */
    stackRole(column: DxcColumn<T>): string | null {
        return column.stack ?? (column.status ? 'aside' : null);
    }

    get hasActions() { return this.actions.length > 0; }
    get filterColumns() { return this.columnFilters ? this.columns.filter(column => column.filter) : []; }
    /** Hay algo que limpiar: búsqueda, filtros o ambos. */
    get hasCriteria() { return !!this.query.trim() || this.activeFilters.length > 0; }
    get skeletonRows() { return Array.from({ length: Math.min(5, this.pageSize) }); }

    trackRow = (row: T) => this.trackBy(row);

    /**
     * Variante del riel de la fila, o `null`. Se deduce de la primera columna
     * marcada con `rail: true`, así que la vista no tiene que acordarse de
     * nada más: un contrato de dos piezas se cumple a medias.
     */
    rowRail(row: T): string | null {
        const column = this.columns.find(c => c.rail && c.status);
        return column?.status?.(row)?.variant ?? null;
    }

    /**
     * La pastilla de la columna que dibuja el riel va `--quiet`, y las demás
     * llenas.
     *
     * Es la economía de color de R11 aplicada por deducción y no por un
     * modificador que la vista tenga que acordarse de poner: con el canto de la
     * fila ya teñido del mismo tono, una píldora rellena gasta el color dos
     * veces.
     *
     * Quien conserva la señal es el TEXTO de la pastilla, y conviene decirlo
     * porque desde que se retiró `.dxc-badge__dot` es lo único que queda además
     * del color: una etiqueta de estado que no se pueda leer —un rótulo vago, o
     * ninguno— deja de distinguirse en escala de grises, y ahí ni el riel ni
     * esto la salvan.
     */
    isQuietBadge(column: DxcColumn<T>): boolean {
        return !!column.rail;
    }

    cellText(column: DxcColumn<T> | undefined, row: T): string {
        if (!column) return '';
        if (column.cell) return column.cell(row);
        const value = (row as any)?.[column.key];
        return value === null || value === undefined ? '' : String(value);
    }

    /** El identificador que encabeza la celda (`DxcColumn.code`), o `''`. */
    cellCode(column: DxcColumn<T>, row: T): string {
        return column.code?.(row) ?? '';
    }

    /**
     * La celda entera como se lee: «CLAVE · descripción». Es el `title` de una
     * columna `truncate`, que es la única forma de leer lo que se cortó.
     */
    cellFull(column: DxcColumn<T>, row: T): string {
        return [this.cellCode(column, row), this.cellText(column, row)].filter(Boolean).join(' · ');
    }

    /**
     * Normaliza para BUSCAR: sin acentos y en minúsculas. Quien teclea
     * «tractocamion» busca «Tractocamión», y exigirle el acento es pedirle que
     * adivine cómo se dio de alta el catálogo.
     */
    private static fold(text: string): string {
        return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    /**
     * Lo que el buscador compara de una fila: lo que la celda enseña —el
     * identificador, el texto y la pastilla— de todas las columnas. Se busca
     * sobre lo que se VE y no sobre el valor crudo, porque es lo único que el
     * usuario puede haber leído: una unidad guardada como «Tractocamión…» se
     * encuentra por «T3S2» porque la columna enseña la clave.
     */
    private searchText(row: T): string {
        return DxcTableComponent.fold(this.columns
            .map(column => `${this.cellCode(column, row)} ${this.cellText(column, row)} ${column.status?.(row)?.label ?? ''}`)
            .join(' '));
    }

    /**
     * Texto por el que se filtra una celda. Se prefiere el valor crudo sobre la
     * etiqueta del estado: en «GPS» el valor es el proveedor, mientras que la
     * etiqueta le añade la referencia y volvería inservible un desplegable —
     * una opción por vehículo. Cuando no hay valor crudo (columna que solo
     * existe como estado, o la fila sin GPS) se cae a la etiqueta.
     */
    filterText(column: DxcColumn<T>, row: T): string {
        return this.cellText(column, row) || (column.status?.(row)?.label ?? '');
    }

    // --- Filtro por columna --------------------------------------------------

    /** Opciones de un filtro `select`: los valores que de verdad están en los datos. */
    filterOptions(column: DxcColumn<T>): DxcSelectOption[] {
        const values = [...new Set(this.#rows.map(row => this.filterText(column, row)).filter(Boolean))]
            .sort((a, b) => a.localeCompare(b, 'es', { numeric: true }));
        return [{ value: '', label: 'Todos' }, ...values.map(value => ({ value, label: value }))];
    }

    get activeFilters(): DxcActiveFilter[] {
        return this.filterColumns
            .filter(column => (this.filters[column.key] ?? '').trim())
            .map(column => ({ key: column.key, label: column.label, value: this.filters[column.key].trim() }));
    }

    setFilter(key: string, value: string) {
        this.filters = { ...this.filters, [key]: value };
        this.page = 1;
        this.filtersChange.emit(this.filters);
        this.pruneSelection();
        this.cdr.markForCheck();
    }

    clearFilter(key: string) { this.setFilter(key, ''); }

    clearFilters() {
        this.filters = {};
        this.page = 1;
        this.filtersChange.emit(this.filters);
        this.cdr.markForCheck();
    }

    /** Lo que hace el botón de la barra y el del estado vacío: dejar la tabla limpia. */
    clearCriteria() {
        this.query = '';
        this.clearFilters();
    }

    // --- Filtro, orden y página ---------------------------------------------

    get filteredRows(): T[] {
        if (this.serverSide) return this.#rows;

        // Cada palabra por separado y en cualquier orden: «T3S2 2021» encuentra
        // el tractocamión de ese año aunque las dos cosas vivan en columnas
        // distintas.
        const words = DxcTableComponent.fold(this.query.trim()).split(/\s+/).filter(Boolean);
        let rows = !words.length ? this.#rows : this.#rows.filter(row => {
            const text = this.searchText(row);
            return words.every(word => text.includes(word));
        });

        for (const { key, value } of this.activeFilters) {
            const column = this.columns.find(c => c.key === key)!;
            const needle = value.toLowerCase();
            rows = rows.filter(row => {
                const text = this.filterText(column, row).toLowerCase();
                return column.filter === 'select' ? text === needle : text.includes(needle);
            });
        }

        if (this.sort) {
            const { key, dir } = this.sort;
            const column = this.columns.find(c => c.key === key);
            rows = [...rows].sort((a, b) => {
                const x = column?.cell ? column.cell(a) : (a as any)?.[key];
                const y = column?.cell ? column.cell(b) : (b as any)?.[key];
                const numeric = typeof x === 'number' && typeof y === 'number';
                return (numeric ? (x as number) - (y as number) : String(x ?? '').localeCompare(String(y ?? ''), 'es')) * dir;
            });
        }
        return rows;
    }

    get totalPages() { return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize)); }

    get pageRows(): T[] {
        if (this.serverSide) return this.filteredRows;
        const page = Math.min(this.page, this.totalPages);
        return this.filteredRows.slice((page - 1) * this.pageSize, page * this.pageSize);
    }

    get pageNumbers() { return Array.from({ length: this.totalPages }, (_, i) => i + 1); }
    get rangeFrom() { return this.filteredRows.length ? (Math.min(this.page, this.totalPages) - 1) * this.pageSize + 1 : 0; }
    get rangeTo() { return Math.min(this.page * this.pageSize, this.filteredRows.length); }

    onSearch(query: string) {
        this.query = query;
        this.page = 1;
        this.pruneSelection();
        this.cdr.markForCheck();
    }

    onSort(key: string) {
        this.sort = this.sort?.key === key
            ? { key, dir: this.sort.dir === 1 ? -1 : 1 }
            : { key, dir: 1 };
        this.sortChange.emit(this.sort);
        this.cdr.markForCheck();
    }

    onHeaderKey(event: KeyboardEvent, key: string) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.onSort(key);
    }

    sortAttr(key: string) {
        if (this.sort?.key !== key) return null;
        return this.sort.dir === 1 ? 'ascending' : 'descending';
    }

    goTo(page: number) {
        this.page = Math.min(Math.max(1, page), this.totalPages);
        this.pageChange.emit(this.page);
        this.cdr.markForCheck();
    }

    // --- Selección -----------------------------------------------------------

    isSelected(row: T) { return this.selection.some(item => this.trackBy(item) === this.trackBy(row)); }

    toggleRow(row: T) {
        this.selection = this.isSelected(row)
            ? this.selection.filter(item => this.trackBy(item) !== this.trackBy(row))
            : [...this.selection, row];
        this.selectionChange.emit(this.selection);
        this.cdr.markForCheck();
    }

    get allOnPageSelected() { return this.pageRows.length > 0 && this.pageRows.every(row => this.isSelected(row)); }

    toggleAllOnPage() {
        const rows = this.pageRows;
        this.selection = this.allOnPageSelected
            ? this.selection.filter(item => !rows.some(row => this.trackBy(row) === this.trackBy(item)))
            : [...this.selection, ...rows.filter(row => !this.isSelected(row))];
        this.selectionChange.emit(this.selection);
        this.cdr.markForCheck();
    }

    /**
     * La selección nunca incluye filas que la búsqueda o los filtros ocultan.
     * Sin esto, seleccionar diez, buscar otra placa y pulsar «Eliminar
     * seleccionados» borraría también las diez que ya no se ven — una acción
     * en bloque solo alcanza lo que está a la vista.
     *
     * Solo en cliente: con `serverSide` la tabla no sabe qué filas cumplen el
     * criterio, y cada página es otro juego de filas.
     */
    private pruneSelection() {
        if (this.serverSide || !this.selection.length) return;
        const visible = new Set(this.filteredRows.map(row => this.trackBy(row)));
        const kept = this.selection.filter(item => visible.has(this.trackBy(item)));
        if (kept.length === this.selection.length) return;
        this.selection = kept;
        this.selectionChange.emit(this.selection);
    }

    clearSelection() {
        this.selection = [];
        this.selectionChange.emit(this.selection);
        this.cdr.markForCheck();
    }
}
